import { TagDescription } from '@reduxjs/toolkit/dist/query/react'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { rdot360Api } from 'store/api/rdot360'
import { apiConstants } from './apis'
import { selectedAccountsApiContextKey } from './rdot360Context'
import {
  GetProjectedIncomeSummaryResponse,
  IRdot360ApiResponse,
  IActivitySummary,
  GetGainLossesIncomeSummaryResponse,
  GetChartProjectedIncomeSummary,
  GetTableProjectedIncomeSummary,
  IGroupedActivitySummary,
  GetIncomeSummaryChartView,
  GetChartEstimatedIncomeSummary,
  ICheck,
  ICheckValue
} from './types'

export interface PerformanceCategory {
  category?: string
  yearToDate?: number
  quarterToDate?: number
  monthToDate?: number
  prior12Months?: number
  inceptionToDate?: number
}

type FinancialsApiTagType =
  | 'activity'
  | 'estimatedIncome'
  | 'projectedIncome'
  | 'income'
  | 'gainandlosss'

const financialsApiTags: FinancialsApiTagType[] = [
  'activity',
  'estimatedIncome',
  'projectedIncome',
  'income',
  'gainandlosss'
]

const contextJsonHeader = selectedAccountsApiContextKey

const { cacheTime } = apiConstants

const rdot360ApiEnhanced = rdot360Api.enhanceEndpoints({
  addTagTypes: financialsApiTags
})
const financialsApi = rdot360ApiEnhanced.injectEndpoints({
  endpoints: (builder) => ({
    getYTDIncomeSummary: builder.query<
      GetChartProjectedIncomeSummary | undefined,
      unknown
    >({
      query: () => {
        const financialYear = new Date().getFullYear()
        return {
          url: `holdings/odata/Position/GetEstimatedIncomeSummary('C',${financialYear})`,
          headers: { contextjson: contextJsonHeader }
        }
      },
      transformResponse: (
        response: IRdot360ApiResponse<GetChartProjectedIncomeSummary>
      ): GetChartProjectedIncomeSummary | undefined => response?.value?.[0],
      providesTags: ['rdot360'],
      keepUnusedDataFor: cacheTime
    }),
    getIncomeSummaryChartView: builder.query<
      GetIncomeSummaryChartView | undefined,
      unknown
    >({
      query: () => {
        return {
          url: `holdings/odata/Position/GetIncomeSummaryChartView(true)`,
          headers: { contextjson: contextJsonHeader }
        }
      },
      transformResponse: (response: GetIncomeSummaryChartView) => response,
      providesTags: ['rdot360'],
      keepUnusedDataFor: cacheTime
    }),
    getChartEstimatedIncomeSummary: builder.query<
      GetChartEstimatedIncomeSummary | undefined,
      { financialYear: number; accounts: string[] }
    >({
      query: ({ financialYear }) => {
        return {
          url: `holdings/odata/Position/GetIncomeMonthlySummary(${financialYear})`,
          headers: { contextjson: contextJsonHeader }
        }
      },
      transformResponse: (
        response: GetChartEstimatedIncomeSummary[]
      ): GetChartEstimatedIncomeSummary | undefined => response?.[0],
      providesTags: ['rdot360', 'income', 'estimatedIncome'],
      keepUnusedDataFor: cacheTime
    }),
    getChartProjectedIncomeSummary: builder.query<
      GetChartProjectedIncomeSummary | undefined,
      unknown
    >({
      query: () => {
        const financialYear = new Date().getFullYear()
        return {
          url: `holdings/odata/Position/GetProjectedIncomeSummary('C',${financialYear})`,
          headers: { contextjson: contextJsonHeader }
        }
      },
      transformResponse: (
        response: GetChartProjectedIncomeSummary[]
      ): GetChartProjectedIncomeSummary | undefined => response?.[0],
      providesTags: ['rdot360', 'income', 'projectedIncome'],
      keepUnusedDataFor: cacheTime
    }),
    getTableProjectedIncomeSummary: builder.query<
      GetTableProjectedIncomeSummary | undefined,
      unknown
    >({
      query: () => {
        const financialYear = new Date().getFullYear()
        return {
          url: `holdings/odata/Position/GetProjectedIncomeSummary('S',${financialYear})`,
          headers: { contextjson: contextJsonHeader }
        }
      },
      transformResponse: (
        response: IRdot360ApiResponse<GetTableProjectedIncomeSummary>
      ): GetTableProjectedIncomeSummary | undefined => response?.value?.[0],
      providesTags: ['rdot360', 'income', 'projectedIncome'],
      keepUnusedDataFor: cacheTime
    }),
    getTableEstimatedIncomeSummary: builder.query<
      GetTableProjectedIncomeSummary | undefined,
      { financialYear: number; accounts: string[] }
    >({
      query: ({ financialYear }) => {
        return {
          url: `holdings/odata/Position/GetEstimatedIncomeSummary('S',${financialYear})`,
          headers: { contextjson: contextJsonHeader }
        }
      },
      transformResponse: (
        response: IRdot360ApiResponse<GetTableProjectedIncomeSummary>
      ): GetTableProjectedIncomeSummary | undefined => response?.value?.[0],
      providesTags: ['rdot360', 'income', 'estimatedIncome'],
      keepUnusedDataFor: cacheTime
    }),
    getAccountProjectedIncomeSummary: builder.query<
      GetProjectedIncomeSummaryResponse | undefined,
      unknown
    >({
      query: () => {
        return {
          url: `holdings/odata/Position/GetEstimatedIncomeSummary('A',2022)`,
          headers: { contextjson: contextJsonHeader }
        }
      },
      transformResponse: (
        response: IRdot360ApiResponse<GetProjectedIncomeSummaryResponse>
      ): GetProjectedIncomeSummaryResponse | undefined => response?.value?.[0],
      providesTags: ['rdot360'],
      keepUnusedDataFor: cacheTime
    }),
    getRealizedGainLossSummary: builder.query<
      GetGainLossesIncomeSummaryResponse | undefined,
      { financialYear: number; accounts: string[] }
    >({
      query: ({ financialYear }) => {
        return {
          url: `holdings/odata/Position/GetRealizedGainLoss('A',${financialYear},'Y','','')`,
          headers: {
            contextjson: contextJsonHeader,
            profilejson: contextJsonHeader
          }
        }
      },
      transformResponse: (
        response: IRdot360ApiResponse<GetGainLossesIncomeSummaryResponse>
      ): GetGainLossesIncomeSummaryResponse | undefined => response?.value?.[0],
      providesTags: ['rdot360', 'gainandlosss'],
      keepUnusedDataFor: cacheTime
    }),
    getRealizedGainLossSecurity: builder.query<
      GetGainLossesIncomeSummaryResponse | undefined,
      unknown
    >({
      query: ({ financialYear }) => {
        return {
          url: `holdings/odata/Position/GetRealizedGainLoss('S',${financialYear},'Y','','')`,
          headers: {
            contextjson: contextJsonHeader,
            profilejson: contextJsonHeader
          }
        }
      },
      transformResponse: (
        response: IRdot360ApiResponse<GetGainLossesIncomeSummaryResponse>
      ): GetGainLossesIncomeSummaryResponse | undefined => response?.value?.[0],
      providesTags: ['rdot360', 'gainandlosss'],
      keepUnusedDataFor: cacheTime
    }),
    getActivitySummary: builder.query<IActivitySummary, string[]>({
      query: () => {
        return {
          url: 'activityapi/odata/activitysummary?AccountList=&SearchBy=LatestActivity,10&DateRange=Last,30',
          headers: {
            contextjson: contextJsonHeader,
            profilejson: contextJsonHeader
          }
        }
      },
      transformResponse: (response: IRdot360ApiResponse<IActivitySummary>) =>
        response?.value?.[0] || {},
      providesTags: ['rdot360'],
      keepUnusedDataFor: cacheTime
    }),
    getDetailActivitySummary: builder.query<
      IActivitySummary,
      { contextAccounts: string[]; categories?: string[]; dateRange?: string }
    >({
      query: ({ categories, dateRange }) => {
        return {
          url: `activityapi/odata/activitysummary?AccountList=&DateRange=${dateRange}${
            categories && categories.length
              ? `&SearchBy=TranCode,${categories.join(',')}`
              : ''
          }`,
          headers: {
            contextjson: contextJsonHeader,
            profilejson: contextJsonHeader
          }
        }
      },
      transformResponse: (response: IRdot360ApiResponse<IActivitySummary>) =>
        response?.value?.[0] || {},
      providesTags: ['rdot360', 'activity'],
      keepUnusedDataFor: cacheTime
    }),
    getActivitySummaryGroup: builder.query<
      IGroupedActivitySummary,
      { contextAccounts: string[]; range: string }
    >({
      query: ({ range }) => {
        return {
          url: `activityapi/odata/activitysummary/group?DateRange=Last,${range}`,
          headers: {
            contextjson: contextJsonHeader,
            profilejson: contextJsonHeader
          }
        }
      },
      transformResponse: (response: IGroupedActivitySummary[]) => response?.[0],
      providesTags: ['rdot360', 'activity'],
      keepUnusedDataFor: cacheTime
    }),
    getPerformance: builder.query<PerformanceCategory[] | undefined, string[]>({
      query: () => {
        return {
          url: `performance`,
          headers: { contextjson: contextJsonHeader }
        }
      },
      transformResponse: (
        response: IRdot360ApiResponse<PerformanceCategory>
      ): PerformanceCategory[] | undefined => response?.value,
      providesTags: ['rdot360'],
      keepUnusedDataFor: cacheTime
    }),
    retrieveCheck: builder.query<
      ICheckValue | undefined,
      {
        AccountList: string
        dateRange?: string
        FilterBy?: string
      }
    >({
      query: ({ AccountList, dateRange, FilterBy }) => ({
        url: `mm/odata/MMSummary?AccountList=${AccountList}&DateRange=${dateRange}&SearchBy=Image,Check,CKR&FilterBy=${FilterBy}`,
        headers: {
          contextjson: contextJsonHeader,
          profilejson: contextJsonHeader
        },
        method: 'GET'
      }),
      transformResponse: (response: ICheck | undefined) => response?.value?.[0],
      keepUnusedDataFor: cacheTime
    })
  })
})
export const {
  useGetRealizedGainLossSummaryQuery,
  useGetRealizedGainLossSecurityQuery,
  useGetActivitySummaryQuery,
  useGetYTDIncomeSummaryQuery,
  useGetChartEstimatedIncomeSummaryQuery,
  useGetChartProjectedIncomeSummaryQuery,
  useGetTableProjectedIncomeSummaryQuery,
  useGetTableEstimatedIncomeSummaryQuery,
  useGetIncomeSummaryChartViewQuery,
  useGetAccountProjectedIncomeSummaryQuery,
  useGetPerformanceQuery,
  useGetDetailActivitySummaryQuery,
  useGetActivitySummaryGroupQuery,
  useRetrieveCheckQuery,
  useLazyRetrieveCheckQuery
} = financialsApi

export const useFinancialsApiUtil = () => {
  const dispatch = useDispatch()
  const invalidateTags = useCallback(
    (tags: TagDescription<FinancialsApiTagType>[]) =>
      dispatch(financialsApi.util.invalidateTags(tags)),
    [dispatch]
  )

  return {
    invalidateTags
  }
}
